import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AppButton,
  Box,
  Contact,
  Inscription,
  Preloader,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import img from '@/assets/backgrounds/ExpressSummaryBg.png';
import { ModalWrapper } from '@/components';

import { ContactPayload } from '../AddEditContactForm/stores/useContact.store';
import AddEditContactModal from '../AddEditContactModal';
import EditAddressModal from '../EditAddressModal';
import { ExpressSetupWizardStepType } from '../ExpressSetup.types';
import ExpressSetupActivateModal from '../ExpressSetupActivateModal';
import { useActivationAllowed, useExpressSetupActions } from '../hooks';

import { ExpressSetupSummaryHeader } from './ExpressSetupSummaryHeader';
import { ExpressSetupSummaryMainTiles } from './ExpressSetupSummaryMainTiles';
import { useExpressSetupExternalIdStore } from './stores';

const StyledLink = styled.a`
  color: ${({ theme }) => theme.colors.textAccent};
  font-size: 14px;
  line-height: 20px;
`;

const LinksBuilder = (links: { link?: string; name: string }[]) => {
  const validLinks = links.filter((item) => item.link);
  const coma = (
    <Inscription
      size='14px'
      lineHeight='20px'
      margin={{ right: 'spacing4' }}
    >
      ,
    </Inscription>
  );
  const and = (twoItems?: boolean) => (
    <Inscription
      size='14px'
      lineHeight='20px'
      margin={{ right: 'spacing4', left: twoItems ? 'spacing4' : undefined }}
    >
      and
    </Inscription>
  );
  if (validLinks.length === 0) return null;
  
  const linkElements = validLinks.map((item) => (
    <StyledLink
      key={item.name}
      href={item.link}
      target="_blank"
      rel="noreferrer"
      data-testid={`ExpressSetupSummaryModal-link-${item.name}`}
    >
      {item.name}
    </StyledLink>
  ));
  
  if (validLinks.length === 1) return linkElements[0];
  if (validLinks.length === 2) {
    return ( 
      <>
        {linkElements[0]}
        {and(true)}
        {linkElements[1]}
      </>
    );
  }
  
  return (
    <>
      {linkElements.slice(0, -1).map((el) => [el, coma])}
      {and()}
      {linkElements[linkElements.length - 1]}
    </>
  );
};

interface Props {
  visible: boolean;
  onSetVisible: (visible: boolean) => void;
  externalId?: string;
}

export const ExpressSetupSummaryModal: React.FC<Props> = ({
  visible,
  onSetVisible,
  externalId,
}) => {
  const [isVisibleExpressSetupPopup, setVisibleActivateExpressSetupPopup] = useState(false);
  const [updatedContact, setUpdatedContact] = useState<ContactPayload>();
  const [contactModalVisible, setContactModalVisible] = useState(false);

  const {
    addressModalVisible,
    expressSetupData,
    isLoading,
    handleRedirectToWizardStep,
    handleOpenAddressModal,
    refetch: refetchExpressSetupData,
  } = useExpressSetupActions(externalId);

  const handleUpdateContact = useCallback((value?: Contact | ContactPayload) => {
    setUpdatedContact(value as ContactPayload);
  },[setUpdatedContact]);

  const handleSetExternalId  = useExpressSetupExternalIdStore((state) => state.handleSetExternalId);
  const handleResetExternalId = useExpressSetupExternalIdStore((state) => state.handleReset);
  
  const isActivationAllowed = useActivationAllowed(expressSetupData);

  const links = useMemo(() => ([
    {
      link: expressSetupData?.pricingConsentLink,
      name: 'Pricing consent',
    },
    {
      link: expressSetupData?.privacyPolicyLink,
      name: 'Privacy Policy',
    },
    {
      link: expressSetupData?.termsConditionsLink,
      name: 'Terms of Use',
    },
  ]), [expressSetupData]);

  const handleOpenCurrentAddressModal = useCallback((value: boolean) => {
    if (externalId && !value) {
      refetchExpressSetupData();
    }

    handleOpenAddressModal(value);
  }, [externalId, handleOpenAddressModal, refetchExpressSetupData]);

  const handleOpenContactModal = useCallback((value: boolean) => {
    if (externalId && !value) {
      refetchExpressSetupData();
    }

    setContactModalVisible(value);
  }, [externalId, refetchExpressSetupData]);

  useEffect(() => {
    if (updatedContact) {
      setContactModalVisible(true);
    }
  }, [updatedContact]);

  useEffect(() => {
    if (externalId && handleSetExternalId) {
      handleSetExternalId(externalId);
    }
    return () => {
      handleResetExternalId();
    };
  }, [externalId, handleSetExternalId, handleResetExternalId]);

  if (contactModalVisible || addressModalVisible) {
    return (
      <>
        <AddEditContactModal
          visible={contactModalVisible}
          onToggleVision={handleOpenContactModal}
          onSetUpdatedContact={handleUpdateContact}
          updatedElement={updatedContact}
          isDisabled={expressSetupData?.isAbandoned}
          summaryMode
        />
        <EditAddressModal
          visible={addressModalVisible}
          onToggleVision={handleOpenCurrentAddressModal}
          address={expressSetupData?.address}
        />
      </>
    );
  }

  if (isVisibleExpressSetupPopup) {
    return (
      <ExpressSetupActivateModal
        expressSetup={expressSetupData}
        isVisible={isVisibleExpressSetupPopup}
        onSetVisible={setVisibleActivateExpressSetupPopup}
        externalId={externalId}
      />
    );
  }

  return (
    <ModalWrapper
      testId="ExpressSetupSummaryModal"
      visible={visible}
      onSetVisible={onSetVisible}
      contentStyle={{
        backgroundImage: `url(${img})`,
        backgroundRepeat: 'no-repeat',
        cursor: 'default',
      }}
    >

      <ExpressSetupSummaryHeader
        expressSetupData={expressSetupData}
        isLoading={isLoading}
        visible={visible}
        handleOnSetAddressModalVisible={handleOpenAddressModal}
        handleOnClickMainContact={() => {
          handleRedirectToWizardStep(ExpressSetupWizardStepType.MainContact);
          onSetVisible(false);
        }}
        handleOnClickOpenContactModal={() => setContactModalVisible(true)}
        handleOnClickUpdateContact={handleUpdateContact}
      />

      <ExpressSetupSummaryMainTiles
        expressSetupData={expressSetupData}
        isLoading={isLoading}
        handleOnClickMainContact={() => {
          handleRedirectToWizardStep(ExpressSetupWizardStepType.MainContact);
          onSetVisible(false);
        }}
        handleOnClickBankAccount={() => {
          handleRedirectToWizardStep(ExpressSetupWizardStepType.BankAccount);
          onSetVisible(false);
        }}
        handleOnClickPlans={() => {
          handleRedirectToWizardStep(ExpressSetupWizardStepType.Plans);
          onSetVisible(false);
        }}
      />

      <Box pad={{ vertical: 'spacing24' }}>
        <AppButton
          testId='activate-organization'
          disabled={isLoading || !isActivationAllowed || expressSetupData?.isAbandoned}
          size="L"
          width="100%"
          onClick={() => {
            setVisibleActivateExpressSetupPopup(true);
            onSetVisible(false);
          }}
        >
          {isLoading ? <Preloader color="primary" /> : 'Activate Organization'}
        </AppButton>
      </Box>

      <Box direction="row">
        {LinksBuilder(links)}
      </Box>
    </ModalWrapper>
  );
};
